/**
 * Public /about page (C23).
 *
 * Explains what Records Monitor is and — per PLAN.md §7 "public-records
 * etiquette" — gives any jurisdiction or member of the public a clear contact
 * path to request a takedown or report a data issue. Flat (public) route: it
 * is reachable without signing in.
 */
import Container from "@mui/material/Container";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { createFileRoute } from "@tanstack/react-router";

/**
 * Project contact address. Mirrors the scraper User-Agent contact in
 * CLAUDE.md §7 (`contact@<domain>`); swapped for the real address at launch.
 */
const CONTACT_EMAIL = "contact@records-monitor.example";

function AboutPage() {
  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Stack spacing={3}>
        <Typography variant="h4" component="h1">
          About Records Monitor
        </Typography>

        <Typography variant="body1">
          Records Monitor scrapes, indexes, and monitors public meeting records — agendas, minutes,
          packets, resolutions, ordinances, and related exhibits — published by local governments in
          Ohio. We make this already-public information searchable by keyword and by
          natural-language question, and we notify people when new records match a saved search.
        </Typography>

        <Typography variant="body1">
          We only collect information that local governments already publish on their own websites,
          and we crawl politely: we respect <code>robots.txt</code>, identify ourselves with a
          contact address, and throttle our requests.
        </Typography>

        <section aria-labelledby="contact-heading">
          <Typography id="contact-heading" variant="h5" component="h2" gutterBottom>
            Contact, takedowns, and data corrections
          </Typography>
          <Typography variant="body1">
            If you represent a jurisdiction, or if you have spotted information that was published
            in error (for example, inadvertently disclosed private data), email us at{" "}
            <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>. We honor takedown
            requests for inadvertently-published private data within 48 hours, and we will correct
            or remove indexed records that do not accurately reflect the source site.
          </Typography>
        </section>

        <Typography variant="body2" color="text.secondary">
          See also our <Link href="/privacy">Privacy Policy</Link>,{" "}
          <Link href="/terms">Terms of Service</Link>, and{" "}
          <Link href="/status">service status</Link>.
        </Typography>
      </Stack>
    </Container>
  );
}

export const Route = createFileRoute("/about")({
  component: AboutPage,
});
